/**
 * components/inspection/AssignmentEditor.js — НЭГ ЗӨРЧИЛ → ОЛОН ХУВИАРЛАЛТ.
 *
 * ⚠ ХОЁР ӨӨР ҮҮРЭГ (2026-09-24):
 *   ХАРИУЦАГЧ   — шалгалтын үед бүртгэгддэг анхан шатны удирдах ажилтан /
 *                 мастер. Зөрчлийг шийдвэрлэхэд АНХААРАХ эзэн; өөрөө
 *                 гүйцэтгэхгүй байж болно (`responsible_tn` — зөрчлийн мөрд).
 *   ГҮЙЦЭТГЭГЧ  — тэр хариуцагч нь ажлыг чиглэлээр нь (цахилгаан · механик ·
 *                 засвар · технологи) хувиарлаж ГҮЙЦЭТГҮҮЛЭХ хүн. Нэг зөрчилд
 *                 хэд хэдэн байж болно; тус бүр ӨӨРИЙН гүйцэтгэлээ хувиар
 *                 оруулна, зөрчлийн гүйцэтгэл нь тэдгээрийн ДУНДАЖ.
 * Энэ цонх нь ГҮЙЦЭТГЭГЧ нарыг удирдана — хариуцагчийг шалгах хуудсан дээр
 * (эсвэл «Хариуцагч оноох» цонхоор) тавина.
 *
 * ЭРХ (backend-д мөн хяналттай):
 *   хувиарлах       — шалгалт засах эрхтэй хүн (`canAssign`)
 *   гүйцэтгэл оруулах — ЗӨВХӨН тухайн мөрийн мастер (эсвэл ХАБЭА)
 *   баталгаажуулах  — шалгагч / ХАБЭА; мастер өөрийгөө хаахгүй
 *
 * Backend: Resources/XAB/item_assignment.py
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Divider,
  Empty,
  Col,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Upload,
  message,
} from "antd";
import moment from "moment";
import WorkerSelect from "src/components/inspection/WorkerSelect";
import { useUserContext } from "src/contexts/userContext";
import { xabItemAssignmentApi } from "src/api/xabApi";
/* ⚠ Зургийг аюулын мэдээллийн upload endpoint-оор — зөрчлийн биелэлтэд
   тусдаа endpoint байхгүй, нэгийг нэмбэл хоёр өөр зам, хоёр өөр хавтас
   болно. `/my-assignments`-ийн биелэлтийн зураг ч ЯГ үүгээр явдаг. */
import { uploadHazardPhoto } from "src/api/hazard";

const INK3 = "#8A94A6";
/* Талбарын жижиг шошго. ⚠ placeholder нь утга сонгомогц АЛГА болдог тул
   дараа нь «энэ сонгогч юу байсан бэ» гэдэг нь мэдэгдэхгүй болно —
   гурван сонгогч зэрэгцэж байхад онцгой төөрөгдөлтэй. */
const FLD = {
  display: "block",
  fontSize: 10,
  fontWeight: 700,
  color: "#5F5E5A",
  letterSpacing: 0.2,
  marginBottom: 2,
};
const BRAND = "#0F6E56";

// Хувиарлах ЧИГЛЭЛ — backend-ийн CATEGORIES-тэй ИЖИЛ түлхүүр
export const ASSIGN_CATEGORIES = [
  { value: "mechanical", label: "Механик", color: "#B45309", icon: "fa-cogs" },
  { value: "electrical", label: "Цахилгаан", color: "#CA8A04", icon: "fa-bolt" },
  { value: "technological", label: "Технологи", color: "#0F6E56", icon: "fa-industry" },
  { value: "repair", label: "Засвар", color: "#1D4ED8", icon: "fa-wrench" },
  { value: "other", label: "Бусад", color: "#6B7280", icon: "fa-ellipsis-h" },
];
export const acatOf = (v) => ASSIGN_CATEGORIES.find((c) => c.value === v) || null;

const ST = {
  open: { label: "Хүлээгдэж буй", color: "#92400E", bg: "#FEF3C7" },
  submitted: { label: "Баталгаажуулах", color: "#3730A3", bg: "#E0E7FF" },
  closed: { label: "Хаагдсан", color: "#166534", bg: "#DCFCE7" },
  rejected: { label: "Буцаасан", color: "#B91C1C", bg: "#FEE2E2" },
};

/** Явцын зураас — жагсаалт, цонх хоёулаа хэрэглэнэ */
export function PercentBar({ percent, overdue, height = 6 }) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const color = overdue ? "#DC2626" : pct >= 100 ? "#16A34A" : pct > 0 ? "#CA8A04" : "#D0D5DD";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ flex: 1, height, borderRadius: height / 2, background: "#EEF0F3", overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: color }} />
      </div>
      <span style={{ fontSize: 10.5, fontWeight: 700, color }}>{pct}%</span>
    </div>
  );
}

export default function AssignmentEditor({
  open,
  item,          // зөрчлийн мөр (id, finding_text, inspection{tseh_code,bn_code,inspector_tn})
  canAssign,     // хувиарлах эрхтэй эсэх
  onClose,
  onSaved,
}) {
  const { user, isAdmin: isAdminFn } = useUserContext();
  const myTn = String(user?.info?.tn || user?.tn || "").replace(/^0+/, "");
  const isAdmin = !!(isAdminFn && isAdminFn());

  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null); // гүйцэтгэл оруулах мөр

  const insp = item?.inspection || {};

  const load = useCallback(() => {
    if (!item?.id) return;
    setLoading(true);
    xabItemAssignmentApi
      .list(item.id)
      .then((d) => {
        setRows((d?.items || []).map((r) => ({ ...r })));
        setReady(d?.ready !== false);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [item?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open || !item) return null;

  const isMine = (r) => r.responsible_tn && String(r.responsible_tn).replace(/^0+/, "") === myTn;
  /* ⚠ ХААГДСАН зөрчил дээр гүйцэтгэл оруулахгүй: `progress` endpoint нь
     мөрийн төлөвийг `submitted`/`open` болгож `_sync_item`-ээр зөрчлийг
     ДАХИН НЭЭНЭ — хаалтын шийдвэр чимээгүй хүчингүй болно. */
  const itemClosed = (item?.fix_status || "open") === "closed";
  const canProgress = (r) => r.id && !itemClosed && (isMine(r) || isAdmin);
  /* Гүйцэтгэлийг ГҮЙЦЭТГЭГЧЭЭС ӨӨР хүн бичсэн эсэх */
  const notMe = (r) =>
    r.fix_user_tn &&
    r.responsible_tn &&
    String(r.fix_user_tn).replace(/^0+/, "") !==
      String(r.responsible_tn).replace(/^0+/, "");
  const canVerify = (r) =>
    r.id &&
    !isMine(r) &&
    (isAdmin || String(insp.inspector_tn || "").replace(/^0+/, "") === myTn);

  const avg = rows.length
    ? Math.round(rows.reduce((a, r) => a + (Number(r.percent) || 0), 0) / rows.length)
    : 0;

  /**
   * ШИНЭ МӨР — ЗӨРЧЛИЙН ХАРИУЦАГЧААР УРЬДЧИЛАН БӨГЛӨНӨ (2026-09-25).
   *
   * ⚠ ЯАГААД: хариуцагчийг шалгах хуудсан дээр «Бүх нэгжээс» цонхоор
   *   аль хэдийн сонгосон байдаг (ихэвчлэн ӨӨР цехийн мастер). Гэтэл энд
   *   мөр нь ХООСОН эхэлдэг тул хэрэглэгч тэр хүнээ ДАХИН хайх ёстой
   *   болдог — бүр боломжгүй ч байв (доорх WorkerSelect нь шалгалтын
   *   нэгжээр хумигддаг байсан; одоо `allowOtherUnits` нэмэгдсэн).
   *
   * ⚠ ХАРИУЦАГЧ ≠ ГҮЙЦЭТГЭГЧ (файлын толгойг үз). Тиймээс энэ нь ЗӨВХӨН
   *   АНХДАГЧ УТГА — солих боломжтой. Хоёр дахь мөрөөс эхлэн гүйцэтгэгчийг
   *   бөглөхгүй (ажлыг өөр хүмүүст хуваах гэж нэмж байгаа), зөвхөн
   *   хугацааг нь өвлүүлнэ.
   */
  const addRow = () =>
    setRows((p) => [
      ...p,
      {
        __new: true,
        fix_category: p.length ? null : item.fix_category || null,
        responsible_tn: p.length ? null : item.responsible_tn || null,
        responsible_name: p.length ? null : item.responsible_name || null,
        due_date: item.due_date || null,
        task_text: "",
        percent: 0,
        status: "open",
      },
    ]);

  const setRow = (i, patch) =>
    setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    const bad = rows.find((r) => !r.responsible_tn);
    if (bad) {
      message.warning("Мөр бүрд гүйцэтгэгчийг сонгоно уу");
      return;
    }
    setSaving(true);
    try {
      const d = await xabItemAssignmentApi.save(item.id, {
        items: rows.map((r) => ({
          id: r.__new ? undefined : r.id,
          fix_category: r.fix_category || null,
          category_text: r.category_text || null,
          responsible_tn: r.responsible_tn || null,
          responsible_name: r.responsible_name || null,
          due_date: r.due_date ? moment(r.due_date).format("YYYY-MM-DD") : null,
          task_text: r.task_text || null,
        })),
      });
      message.success(`Хадгаллаа — гүйцэтгэл ${d?.percent ?? 0}%`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Хадгалж чадсангүй");
    } finally {
      setSaving(false);
    }
  };

  const verify = async (r, action) => {
    let note = "";
    if (action === "reject") {
      note = window.prompt("Буцаах шалтгаан:") || "";
      if (!note.trim()) return;
    }
    try {
      await xabItemAssignmentApi.verify(r.id, { action, note });
      message.success(action === "accept" ? "Хүлээн авлаа" : "Буцаалаа");
      load();
      onSaved?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Үйлдэл хийж чадсангүй");
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      width={900}
      style={{ top: 20 }}
      title={
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {/* ⚠ Хаагдсан зөрчилд цонх нь ЗӨВХӨН ХАРАХ горимтой —
                гарчиг нь «хувиарлах» гэж хэлсээр байвал хэрэглэгч
                засаж чадна гэж ойлгоно (2026-10-08). */}
            {canAssign
              ? "Зөрчлийг гүйцэтгэгчид хувиарлах"
              : "Хувиарлалт · зөвхөн харах"}
          </div>
          <div style={{ fontSize: 11.5, color: INK3, fontWeight: 400 }}>
            {item.finding_text || item.item_text}
          </div>
          {item.responsible_name ? (
            <div style={{ fontSize: 11, color: INK3, fontWeight: 400, marginTop: 2 }}>
              <i className="fas fa-user-shield" style={{ marginRight: 5, color: BRAND }} />
              Хариуцагч (шийдвэрлэлтэд анхаарна):{" "}
              <b style={{ color: "#3A4356" }}>{item.responsible_name}</b>
            </div>
          ) : null}
        </div>
      }
      footer={
        <Space>
          <span style={{ fontSize: 12, color: INK3, marginRight: "auto" }}>
            Нийт гүйцэтгэл: <b style={{ color: BRAND }}>{avg}%</b> ({rows.length} ажил)
          </span>
          <Button onClick={onClose}>Хаах</Button>
          {canAssign ? (
            <Button type="primary" loading={saving} onClick={save} style={{ background: BRAND, borderColor: BRAND }}>
              Хадгалах
            </Button>
          ) : null}
        </Space>
      }
      destroyOnClose
    >
      {!ready ? (
        <Empty
          description="Хувиарлалтын хүснэгт үүсээгүй байна — SQL/2026-09-23_inspection_item_assignment.sql-ыг ажиллуулна уу."
          style={{ padding: 30 }}
        />
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <PercentBar percent={avg} height={8} />
            <div style={{ fontSize: 11, color: INK3, marginTop: 3 }}>
              Нийт гүйцэтгэл нь хувиарласан ажлуудын дунджаар бодогдоно.
              Гүйцэтгэлийг <b>гүйцэтгэгч бүр өөрөө</b> оруулна.
            </div>
          </div>
          <Divider style={{ margin: "10px 0" }} />

          {rows.length === 0 ? (
            <Empty description="Хувиарлаагүй байна" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : null}

          {rows.map((r, i) => {
            const st = ST[r.status || "open"] || ST.open;
            const cm = acatOf(r.fix_category);
            return (
              <div
                key={r.id || "n" + i}
                style={{
                  border: "1px solid #E6E9EF",
                  borderLeft: `4px solid ${cm?.color || "#D0D5DD"}`,
                  borderRadius: 10,
                  padding: 10,
                  marginBottom: 8,
                  background: "#FCFCFD",
                }}
              >
                {/* Мөрийн толгой — хэд дэх ажил, ямар чиглэл.
                    ⚠ Нэг зөрчлийг 2-3 хүнд өгөхөд мөрүүд ялгарахгүй бол
                      «аль нь хэнийх вэ» гэдэг нь эндүүрэгддэг. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: cm?.color || "#D0D5DD",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "#3A4356" }}>
                    {r.responsible_name || "Гүйцэтгэгч сонгоогүй"}
                  </span>
                  {cm ? (
                    <Tag
                      style={{
                        margin: 0,
                        background: (cm.color || "#8A94A6") + "1A",
                        color: cm.color,
                        border: "none",
                        fontWeight: 600,
                        fontSize: 10.5,
                      }}
                    >
                      {cm.label}
                    </Tag>
                  ) : null}
                  <Tag
                    style={{
                      margin: 0,
                      background: st.bg,
                      color: st.color,
                      border: "none",
                      fontWeight: 600,
                      fontSize: 10.5,
                    }}
                  >
                    {st.label}
                  </Tag>
                  {r.__new ? (
                    <Tag color="blue" style={{ margin: 0, fontSize: 10.5 }}>
                      шинэ
                    </Tag>
                  ) : null}
                  {canAssign ? (
                    <Popconfirm
                      title="Энэ ажлыг хасах уу?"
                      okText="Тийм"
                      cancelText="Үгүй"
                      onConfirm={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                    >
                      <Button
                        danger
                        type="text"
                        size="small"
                        style={{ marginLeft: "auto" }}
                        icon={<i className="fas fa-trash" />}
                      />
                    </Popconfirm>
                  ) : null}
                </div>

                {/* Талбарууд — ТОГТМОЛ сүлжээ (flex-wrap биш): багана нь
                    мөр бүрд ижил байрлалд таарч, нүдээр гүйлгэж уншина. */}
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={8} md={6}>
                    <span style={FLD}>Чиглэл</span>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Сонгох"
                      disabled={!canAssign}
                      value={r.fix_category || undefined}
                      onChange={(v) => setRow(i, { fix_category: v })}
                      options={ASSIGN_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
                    />
                  </Col>
                  <Col xs={24} sm={16} md={12}>
                    <span style={FLD}>Гүйцэтгэгч — ажлыг хийх хүн</span>
                    <WorkerSelect
                      size="middle"
                      disabled={!canAssign}
                      tsehCode={insp.tseh_code}
                      bnCode={insp.bn_code}
                      // ⚠ ӨӨР НЭГЖИЙН хүн сонгох боломж (2026-09-25).
                      //   Цахилгааны зөрчлийг Цахилгаан цех, өргөх
                      //   механизмынхыг ЗМЗ гүйцэтгэдэг. Шалгалтын нэгжээр
                      //   хатуу хумибал тэр хүн ОГТ олдохгүй тул урьдчилан
                      //   бөглөсөн хариуцагчийг цэвэрлэсний дараа буцааж
                      //   сонгох ч аргагүй болдог байв.
                      allowOtherUnits
                      placeholder="Гүйцэтгэгч — ажлыг хийх хүн"
                      value={r.responsible_tn || undefined}
                      initialWorker={
                        r.responsible_tn
                          ? { tn: r.responsible_tn, name: r.responsible_name }
                          : null
                      }
                      onChange={(tn, w) =>
                        setRow(i, { responsible_tn: tn || null, responsible_name: w?.name || null })
                      }
                    />
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <span style={FLD}>Хугацаа</span>
                    <DatePicker
                      style={{ width: "100%" }}
                      placeholder="Огноо"
                      disabled={!canAssign}
                      value={r.due_date ? moment(r.due_date) : null}
                      onChange={(d) => setRow(i, { due_date: d ? d.format("YYYY-MM-DD") : null })}
                    />
                  </Col>
                  <Col xs={24}>
                    <span style={FLD}>Даалгавар (сонголтоор)</span>
                    <Input
                      placeholder="Юуг хийх вэ"
                      disabled={!canAssign}
                      value={r.task_text || ""}
                      onChange={(e) => setRow(i, { task_text: e.target.value })}
                    />
                  </Col>
                </Row>

                {/* ── ГҮЙЦЭТГЭЛ — мастер өөрөө оруулна ──
                    Төлөвийн шошго нь мөрийн ТОЛГОЙД зөөгдсөн (давхардахгүй). */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    marginTop: 10,
                    paddingTop: 8,
                    borderTop: "1px dashed #E6E9EF",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 10.5, color: INK3, fontWeight: 600 }}>
                    Гүйцэтгэл
                  </span>
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <PercentBar percent={r.percent} overdue={r.is_overdue} />
                  </div>
                  {/* ⚠ ХЭН БИЧСЭН нь ГҮЙЦЭТГЭГЧЭЭС ӨӨР байж болно (ХАБЭА
                      цаасаар ирсэн биелэлтийг бүртгэх үед). Өмнө нь зүгээр
                      нэр гардаг тул «Б.Баттулга 100%» гэж харагдаад
                      үнэндээ өөр хүн бичсэн нь мэдэгдэхгүй байв. */}
                  {r.fix_user_name ? (
                    <span
                      style={{
                        fontSize: 10.5,
                        color: notMe(r) ? "#B45309" : INK3,
                        fontWeight: notMe(r) ? 700 : 400,
                      }}
                      title={
                        notMe(r)
                          ? `${r.fix_user_name} нь ${r.responsible_name || "гүйцэтгэгч"}-ийн өмнөөс оруулсан`
                          : undefined
                      }
                    >
                      {notMe(r) ? "оруулсан: " : ""}
                      {r.fix_user_name}
                      {r.fix_date ? ` · ${moment(r.fix_date).format("MM-DD")}` : ""}
                    </span>
                  ) : null}
                  {canProgress(r) ? (
                    <Tooltip
                      title={
                        isMine(r)
                          ? "Өөрийн ажлын гүйцэтгэлээ оруулах"
                          : `${r.responsible_name || "Гүйцэтгэгч"}-ийн өмнөөс оруулна — бүртгэлд ТАНЫ нэр үлдэнэ`
                      }
                    >
                      <Button
                        size="small"
                        danger={!isMine(r)}
                        onClick={() => {
                          /* ⚠ БУСДЫН ажлын гүйцэтгэлийг оруулахад БАТАЛГАА
                             асууна. Хориглохгүй (ХАБЭА-д бодитоор
                             хэрэгтэй) ч санамсаргүй дарахаас сэргийлнэ. */
                          if (isMine(r)) return setProgress(r);
                          Modal.confirm({
                            title: "Өөр хүний гүйцэтгэлийг оруулах уу?",
                            content: `${r.responsible_name || "Гүйцэтгэгч"}-ийн ажлын гүйцэтгэлийг та оруулж байна. Бүртгэлд «оруулсан: таны нэр» гэж үлдэнэ.`,
                            okText: "Тийм, оруулна",
                            cancelText: "Болих",
                            onOk: () => setProgress(r),
                          });
                        }}
                      >
                        <i className="fas fa-percent" style={{ marginRight: 5 }} />
                        {isMine(r) ? "Гүйцэтгэл" : "Өмнөөс нь оруулах"}
                      </Button>
                    </Tooltip>
                  ) : null}
                  {canVerify(r) && r.status === "submitted" ? (
                    <Space size={4}>
                      <Button size="small" type="primary" onClick={() => verify(r, "accept")}>
                        Хүлээн авах
                      </Button>
                      <Button size="small" danger onClick={() => verify(r, "reject")}>
                        Буцаах
                      </Button>
                    </Space>
                  ) : null}
                </div>
                {r.fix_note ? (
                  <div style={{ fontSize: 11.5, color: "#5B6472", marginTop: 4 }}>{r.fix_note}</div>
                ) : null}
                {r.reject_note ? (
                  <div style={{ fontSize: 11.5, color: "#B91C1C", marginTop: 4 }}>
                    Буцаасан: {r.reject_note}
                  </div>
                ) : null}
                {r.__new ? (
                  <div style={{ fontSize: 10.5, color: INK3, marginTop: 4 }}>
                    Хадгалсны дараа энэ гүйцэтгэгч өөрийн гүйцэтгэлээ оруулна.
                  </div>
                ) : null}
                
              </div>
            );
          })}

          {canAssign ? (
            <Button type="dashed" block onClick={addRow} icon={<i className="fas fa-plus" style={{ marginRight: 6 }} />}>
              Ажил нэмэх (өөр чиглэл / өөр гүйцэтгэгч)
            </Button>
          ) : null}
        </>
      )}

      <ProgressModal
        row={progress}
        onClose={() => setProgress(null)}
        onSaved={() => {
          setProgress(null);
          load();
          onSaved?.();
        }}
      />
    </Modal>
  );
}

/** Мастер өөрийн ажлын гүйцэтгэлийг хувиар оруулна */
function ProgressModal({ row, onClose, onSaved }) {
  const [pct, setPct] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  /* ⚠ ХИЙГДСЭН АЖЛЫН ЗУРАГ (2026-10-02): «50% хийлээ» гэсэн тоо нь
     БАРИМТГҮЙ үг — шалгагч баталгаажуулахын тулд ажлыг харах ёстой.
     Backend нь `photo_url`-ыг аль хэдийн хүлээж авдаг
     (item_assignment.py → `row.fix_photo_url`), зөвхөн UI байгаагүй. */
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (row) {
      setPct(Number(row.percent) || 0);
      setNote("");
      // Урьд оруулсан зураг байвал хадгална — дарж бичихгүй
      setPhotoUrl(row.fix_photo_url || null);
    }
  }, [row]);

  const onPickPhoto = async (file) => {
    if (!/^image\//.test(file.type || "")) {
      message.warning("Зөвхөн зураг хавсаргана");
      return false;
    }
    setUploading(true);
    try {
      const d = await uploadHazardPhoto(file);
      if (!d?.file_url) throw new Error("url");
      setPhotoUrl(d.file_url);
    } catch (e) {
      message.error(e?.response?.data?.message || "Зураг илгээж чадсангүй");
    } finally {
      setUploading(false);
    }
    return false; // antd-ийн өөрийн upload-ыг ХААНА
  };

  if (!row) return null;

  const save = async () => {
    setBusy(true);
    try {
      const d = await xabItemAssignmentApi.progress(row.id, {
        percent: pct,
        note,
        photo_url: photoUrl || undefined,
      });
      message.success(`Хадгаллаа — зөрчлийн гүйцэтгэл ${d?.percent ?? pct}%`);
      onSaved();
    } catch (e) {
      message.error(e?.response?.data?.message || "Хадгалж чадсангүй");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={save}
      confirmLoading={busy}
      okText="Хадгалах"
      cancelText="Болих"
      width={460}
      title="Гүйцэтгэл оруулах"
      destroyOnClose
    >
      <div style={{ fontSize: 12, color: INK3, marginBottom: 6 }}>
        {acatOf(row.fix_category)?.label || "Ажил"} · {row.responsible_name}
      </div>
      {row.task_text ? (
        <div style={{ fontSize: 12.5, marginBottom: 8 }}>{row.task_text}</div>
      ) : null}
      <div style={{ fontSize: 12, color: INK3, marginBottom: 4 }}>Гүйцэтгэл (%)</div>
      <InputNumber
        min={0}
        max={100}
        step={5}
        style={{ width: 140 }}
        value={pct}
        onChange={(v) => setPct(Number(v) || 0)}
      />
      <div style={{ marginTop: 8 }}>
        <PercentBar percent={pct} height={8} />
      </div>
      <div style={{ fontSize: 12, color: INK3, margin: "10px 0 4px" }}>Тайлбар</div>
      <Input.TextArea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Юу хийсэн, юу үлдсэн"
      />
      {/* ── ХИЙГДСЭН АЖЛЫН ЗУРАГ ───────────────────────────────────── */}
      <div style={{ fontSize: 12, color: INK3, margin: "10px 0 4px" }}>
        Хийгдсэн ажлын зураг
      </div>
      {photoUrl ? (
        <div style={{ position: "relative", display: "inline-block" }}>
          <a href={photoUrl} target="_blank" rel="noreferrer">
            <img
              src={photoUrl}
              alt="Хийгдсэн ажил"
              style={{
                maxWidth: "100%",
                maxHeight: 180,
                borderRadius: 6,
                border: "1px solid #E6E9EF",
                display: "block",
              }}
            />
          </a>
          <Button
            size="small"
            danger
            onClick={() => setPhotoUrl(null)}
            style={{ position: "absolute", top: 4, right: 4 }}
          >
            ✕
          </Button>
        </div>
      ) : (
        <Upload beforeUpload={onPickPhoto} showUploadList={false} accept="image/*">
          <Button size="small" loading={uploading}>
            <i className="fas fa-camera" style={{ marginRight: 6 }} />
            {uploading ? "Илгээж байна…" : "Зураг хавсаргах"}
          </Button>
        </Upload>
      )}
      <div style={{ fontSize: 11, color: INK3, marginTop: 8 }}>
        100% болгоход шалгагч / ХАБЭА баталгаажуулна. Дутуу хувь оруулбал
        ажил «хүлээгдэж буй» хэвээр үлдэж, зөрчлийн нийт хувьд тооцогдоно.
        <div style={{ marginTop: 2 }}>
          Зураг нь баталгаажуулахад <b>шийдвэрлэх баримт</b> — хувь нь
          зөвхөн тоо, зураг нь хийсэн ажлыг харуулна.
        </div>
      </div>
    </Modal>
  );
}
